import chalk from 'chalk';
import ora from 'ora';
import { setActiveTeam, getActiveTeam } from '../lib/config.js';
import { api } from '../lib/api-client.js';
import { requireAuth } from '../lib/auth-guard.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

// ---------------------------------------------------------------------------
// Subcommands
// ---------------------------------------------------------------------------

/**
 * `agt team list`
 * Fetch teams where the current user is a member and display as a table.
 */
export async function teamListCommand(): Promise<void> {
  if (!requireAuth()) return;

  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching teams\u2026', isSilent: json });
  spinner.start();

  try {
    const data = await api.get<{
      teams: Array<{
        id: string;
        name: string;
        slug: string;
        plan: string;
        role: string;
        created_at: string;
      }>;
    }>('/teams');

    spinner.stop();

    if (!data.teams || data.teams.length === 0) {
      if (json) {
        jsonOutput({ ok: true, teams: [] });
      } else {
        info('You are not a member of any teams. Create one with `agt team create <name>`.');
      }
      return;
    }

    const activeSlug = getActiveTeam();

    if (json) {
      const teams = data.teams.map((ws) => ({
        name: ws.name,
        slug: ws.slug,
        role: ws.role,
        active: ws.slug === activeSlug,
      }));
      jsonOutput({ ok: true, teams });
      return;
    }

    const rows = data.teams.map((ws) => {
      const active = ws.slug === activeSlug ? chalk.green('*') : '';
      return [active, ws.name, ws.slug, ws.role];
    });

    table(['', 'Name', 'Slug', 'Role'], rows);
  } catch (err) {
    spinner.fail('Failed to fetch teams.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

/**
 * `agt team create <name>`
 * Create a new team, add the creator as owner, and set it as active.
 */
export async function teamCreateCommand(name: string): Promise<void> {
  if (!requireAuth()) return;

  const json = isJsonMode();
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!slug) {
    if (json) {
      jsonOutput({ ok: false, error: 'Invalid team name' });
    } else {
      error('Invalid team name. It must contain at least one alphanumeric character.');
    }
    process.exitCode = 1;
    return;
  }

  const spinner = ora({ text: `Creating team "${name}" (${slug})\u2026`, isSilent: json });
  spinner.start();

  try {
    const data = await api.post<{
      team: { id: string; name: string; slug: string; plan: string; role: string };
    }>('/teams', { name, slug });

    // Set as active
    setActiveTeam(data.team.slug);

    spinner.succeed(`Team "${chalk.bold(name)}" created.`);

    if (json) {
      jsonOutput({ ok: true, name, slug: data.team.slug, role: data.team.role });
      return;
    }

    info(`Slug: ${chalk.bold(data.team.slug)}`);
    success(`Active team set to ${chalk.bold(data.team.slug)}.`);
  } catch (err) {
    spinner.fail('Failed to create team.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

/**
 * `agt team switch <slug>`
 * Verify the user is a member of the team and set it as active.
 */
export async function teamSwitchCommand(slug: string): Promise<void> {
  if (!requireAuth()) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Switching to team "${slug}"\u2026`, isSilent: json });
  spinner.start();

  try {
    await api.get(`/teams/${encodeURIComponent(slug)}`);

    setActiveTeam(slug);
    spinner.succeed(`Active team set to ${chalk.bold(slug)}.`);

    if (json) {
      jsonOutput({ ok: true, team: slug });
    }
  } catch (err) {
    spinner.fail(`Could not switch to team "${slug}".`);
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}
